import { useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useAuthStore } from '@/features/auth/store';
import { restaurantApi } from '../api';
import { isOnboardingComplete } from '../utils/onboardingUtils';
import { defaultOnboardingSteps } from '../utils/onboardingSteps';
import type { RestaurantResponse } from '../types';

interface OnboardingGuardProps {
  children: React.ReactNode;
}

/**
 * Guard component that redirects to onboarding if restaurant setup is incomplete.
 *
 * Deliberately avoids the `hasChecked` pattern: that pattern caused a spinner
 * unmount/remount on every guard mount, which corrupted Radix portal DOM nodes
 * and produced "removeChild" crashes. We now only block rendering when we have
 * no cached data at all — cached data lets children render immediately.
 */
export function OnboardingGuard({ children }: OnboardingGuardProps) {
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const token = useAuthStore((s) => s.token);

  const { data: restaurant, isLoading } = useQuery<RestaurantResponse>({
    queryKey: ['restaurant', restaurantId],
    queryFn: () => restaurantApi.getRestaurant(token, restaurantId!),
    enabled: !!token && !!restaurantId,
    staleTime: 30000,
  });

  const isOnboardingRoute = location.pathname.includes('/onboarding');

  useEffect(() => {
    if (isLoading || !restaurant) return;

    const isComplete = isOnboardingComplete(defaultOnboardingSteps, restaurant);

    if (!isComplete && !isOnboardingRoute) {
      navigate(`/app/restaurants/${restaurantId}/onboarding`, { replace: true });
    } else if (isComplete && isOnboardingRoute) {
      navigate(`/app/restaurants/${restaurantId}`, { replace: true });
    }
  }, [restaurant, isLoading, isOnboardingRoute, restaurantId, navigate]);

  if (isLoading && !restaurant) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-accent" />
          <p className="text-muted-foreground text-sm">Loading restaurant...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
